import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download, Mail, Phone, Globe, ArrowUpRight,
  ShoppingBag, Instagram,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

// lucide has no TikTok glyph — inline the brand mark (inherits currentColor).
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.44 0c.35 1.86 1.4 3.32 3.2 4.04.7.28 1.45.42 2.2.46v4.03c-1.43-.04-2.85-.35-4.13-.98-.56-.27-1.08-.6-1.62-.92-.01 2.92.01 5.84-.02 8.75-.1 1.66-.78 3.3-1.97 4.5-1.84 1.9-4.78 2.45-7.2 1.37-2.46-1.07-4.08-3.66-3.93-6.35.08-2.62 1.78-5.04 4.2-5.97 1.18-.47 2.48-.55 3.72-.32 0 1.48-.06 2.96-.06 4.44-.86-.28-1.86-.25-2.64.27-.6.38-1.05.99-1.25 1.67-.16.49-.12 1.03-.1 1.54.27 1.62 1.93 2.84 3.56 2.6 1.1-.13 2.06-.92 2.46-1.94.12-.32.18-.66.18-1V0h3.4z"/>
  </svg>
);

/* ──────────────────────────────────────────────────────────────────
   Digital business card — a standalone, mobile-first page meant to be
   shared via a link (davidmedia.de/card). One tap on "Kontakt speichern"
   downloads a .vcf so the recipient has David in their phone contacts.
   Everything editable lives in CARD below.
   TODO(David): real Instagram/YouTube/TikTok handles, optional photo +
   phone number.
   ────────────────────────────────────────────────────────────────── */
const CARD = {
  brand: 'DAVIDMEDIA',
  fullName: 'David Ferfers',
  role: 'Video Editor & Motion Designer',
  tagline: 'Brands · Stories · Tools',
  email: 'contact@davidmedia.de',
  phone: '', // e.g. '+49 ...' — leave '' to hide
  website: 'https://www.davidmedia.de',
  logo: '/logo-badge.png',
  // Stacked link buttons. internal:true → in-app route; else opens in a new tab.
  links: [
    { label: 'Shop', sub: 'Tools & Extensions', href: '/shop', internal: true, icon: ShoppingBag },
    { label: 'Instagram', sub: '@started.david', href: 'https://www.instagram.com/started.david', icon: Instagram },
    { label: 'TikTok', sub: '@started.david', href: 'https://www.tiktok.com/@started.david', icon: TikTokIcon },
  ],
};

function buildVCard() {
  const [firstName, ...rest] = CARD.fullName.split(' ');
  const lastName = rest.join(' ');
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${lastName};${firstName};;;`,
    `FN:${CARD.fullName}`,
    `ORG:${CARD.brand}`,
    `TITLE:${CARD.role}`,
    `EMAIL;TYPE=INTERNET:${CARD.email}`,
    CARD.phone ? `TEL;TYPE=CELL:${CARD.phone}` : null,
    `URL:${CARD.website}`,
    'END:VCARD',
  ].filter(Boolean).join('\r\n');
}

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.055, delayChildren: 0.05 } },
};
const fade = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function DigitalCard() {
  const navigate = useNavigate();

  const saveContact = () => {
    const blob = new Blob([buildVCard()], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${CARD.brand}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onLink = (link) => {
    if (link.internal) navigate(link.href);
    else window.open(link.href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center px-5 py-12 overflow-hidden"
         style={{ background: '#030303' }}>
      <PageMeta
        title="DAVIDMEDIA — Digitale Visitenkarte"
        description="Video Editor & Motion Designer. Shop, Social & Kontakt auf einen Blick."
        url="https://www.davidmedia.de/card"
      />

      {/* Brand glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[55vh]"
           style={{ background: 'radial-gradient(120% 80% at 50% 0%, rgba(249,115,22,0.22), transparent 70%)' }} />

      <motion.div
        initial="hidden" animate="show" variants={container}
        className="relative w-full max-w-sm rounded-[28px] p-7 sm:p-8"
        style={{
          background: 'rgba(255,255,255,0.035)',
          border: '1px solid rgba(255,255,255,0.10)',
          backdropFilter: 'blur(18px)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8), 0 0 60px -30px rgba(249,115,22,0.5)',
        }}
      >
        {/* Avatar / logo */}
        <motion.div custom={0} variants={fade} className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full blur-xl"
                 style={{ background: 'rgba(249,115,22,0.5)' }} />
            <img
              src={CARD.logo}
              alt={CARD.brand}
              className="relative w-24 h-24 rounded-full object-cover"
              style={{ border: '2px solid rgba(249,115,22,0.6)', background: '#0a0a0a' }}
            />
          </div>
        </motion.div>

        {/* Name + role */}
        <motion.h1 custom={1} variants={fade}
          className="font-syne font-extrabold text-3xl text-white text-center mt-5 tracking-tight">
          {CARD.brand}
        </motion.h1>
        <motion.p custom={2} variants={fade}
          className="font-manrope text-center mt-1.5" style={{ color: 'var(--primary-text, #FF9D5C)' }}>
          {CARD.role}
        </motion.p>
        <motion.p custom={3} variants={fade}
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-center mt-2 text-white/40">
          {CARD.tagline}
        </motion.p>

        {/* Primary: save contact (vCard) */}
        <motion.button custom={4} variants={fade}
          onClick={saveContact}
          className="btn-shine group w-full mt-7 flex items-center justify-center gap-2 h-12 rounded-full font-bold text-sm text-black"
          style={{ background: 'var(--primary, #F97316)' }}
        >
          <Download className="w-4 h-4" />
          Kontakt speichern
        </motion.button>

        {/* Link buttons */}
        <div className="mt-3 space-y-2.5">
          {CARD.links.map((link, i) => {
            const Icon = link.icon || Globe;
            return (
              <motion.button
                key={link.label}
                custom={5 + i} variants={fade}
                onClick={() => onLink(link)}
                className="group w-full flex items-center gap-3 px-4 h-[58px] rounded-2xl text-left transition-colors duration-300"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
                      style={{ background: 'rgba(249,115,22,0.12)', color: 'var(--primary, #F97316)' }}>
                  <Icon className="w-[18px] h-[18px]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-manrope font-semibold text-[15px] text-white leading-tight">{link.label}</span>
                  {link.sub && <span className="block font-manrope text-xs text-white/45 leading-tight truncate">{link.sub}</span>}
                </span>
                <ArrowUpRight className="w-4 h-4 text-white/30 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </motion.button>
            );
          })}
        </div>

        {/* Contact row */}
        <motion.div custom={9} variants={fade} className="flex items-center justify-center gap-3 mt-5">
          <a href={`mailto:${CARD.email}`}
             className="flex items-center justify-center w-11 h-11 rounded-full transition-colors"
             style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
             aria-label="E-Mail">
            <Mail className="w-[18px] h-[18px]" />
          </a>
          {CARD.phone && (
            <a href={`tel:${CARD.phone.replace(/\s/g, '')}`}
               className="flex items-center justify-center w-11 h-11 rounded-full"
               style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
               aria-label="Telefon">
              <Phone className="w-[18px] h-[18px]" />
            </a>
          )}
          <a href={CARD.website} target="_blank" rel="noopener noreferrer"
             className="flex items-center justify-center w-11 h-11 rounded-full"
             style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
             aria-label="Website">
            <Globe className="w-[18px] h-[18px]" />
          </a>
        </motion.div>

        <motion.p custom={10} variants={fade}
          className="font-mono text-[10px] text-center mt-5 text-white/30 tracking-wider">
          davidmedia.de
        </motion.p>
      </motion.div>
    </div>
  );
}
